import { FC, Fragment, useState } from 'react';
import { BookingModel, Gross } from 'ts/transfer/interfaces/TransferResultsResponse';
import TransferBookingForm from './BookingForm';
import TransferResultInfo from '../Result/ResultInfo';
import TransferBookingPreview from './BookingPreview';
import { updatePaymentBlock } from 'services/transfer';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import { faArrowCircleLeft, faSearch } from '@fortawesome/free-solid-svg-icons';

interface TransferBookingProps {
  bookingModel: BookingModel;
  saveScreenshot: (name: string) => void;
  onUpdate: (model: BookingModel) => void;
  onClickBack: () => void;
  onClickSearch: () => void;
}

const TransferBooking: FC<TransferBookingProps> = ({ bookingModel, ...props }) => {
  const [step, setStep] = useState(0);
  const [companyId, setCompanyId] = useState('');
  const [tripId, setTripId] = useState('0');
  const [couponAmount, setCouponAmount] = useState(0);
  const [urlParams] = useSearchParams();
  const { i18n, t } = useTranslation();

  const onBookConinue = async (model: BookingModel) => {
    setStep(1);

    props.onUpdate(model);
    await props.saveScreenshot('book1');
  };

  const recountTotalPrice = async () => {
    const res = await updatePaymentBlock({
      packageCurrency: bookingModel.currency,
      sourcePrice: bookingModel.credit2000PaymentInfo?.fullPriceInShekel || 0,
      addPrice: couponAmount,
      searchToken: urlParams.get('searchToken') || '',
      lang: i18n.language,
    });
    if (res) {
      props.onUpdate({
        ...bookingModel,
        credit2000PaymentInfo: res.credit2000PaymentInfo,
      });
    }
  };

  return (
    <div className="transfer-results">
      <div className="flex-column w100p">
        {step === 0 && (
          <div className="mb-20 default-shadow flex-between dyn-change-header">
            <Button
              title={t('back')}
              icon={faArrowCircleLeft}
              onClick={props.onClickBack}
              className="btn btn-link"
            />
            <Button
              title={t('search.new')}
              icon={faSearch}
              onClick={props.onClickSearch}
              className="btn btn-link"
            />
          </div>
        )}
        {step === 1 && (
          <div className="mb-20 default-shadow flex-between dyn-change-header">
            <Button
              title={t('back')}
              icon={faArrowCircleLeft}
              onClick={() => setStep(0)}
              className="btn btn-link"
            />
          </div>
        )}
        <div className="flex-between w100p">
          {step === 0 && (
            <Fragment>
              <TransferBookingForm
                bookingModel={bookingModel}
                onBookConinue={onBookConinue}
                model={bookingModel?.owner}
                setCompanyId={setCompanyId}
                setTripId={setTripId}
                companyId={companyId}
                tripId={tripId}
              />
              <TransferResultInfo
                mapTransfer={bookingModel.forward.transfer}
                choosenArrival={bookingModel.forward}
                choosenReturn={bookingModel.return}
                oneWay={!bookingModel.return}
                currency={bookingModel.currency.toLowerCase() as keyof Gross}
              />
            </Fragment>
          )}
          {step === 1 && (
            <Fragment>
              <TransferBookingPreview
                bookingModel={bookingModel}
                ownerModel={bookingModel.owner}
                onClickBook={() => props.saveScreenshot('book2')}
                companyId={companyId}
                tripId={tripId}
                couponAmount={couponAmount}
              />
              <TransferResultInfo
                mapTransfer={bookingModel.forward.transfer}
                oneWay={!bookingModel.return}
                currency={bookingModel.currency.toLowerCase() as keyof Gross}
                couponAmount={couponAmount}
                setCouponAmount={setCouponAmount}
                recountTotalPrice={recountTotalPrice}
                totalPrice={bookingModel.totalPrice}
              />
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(TransferBooking, 'TransferBooking');
